import React, { FC } from "react";
import { QuestionMarkCircleIcon } from "@heroicons/react/solid";
import { PureTooltip } from "components/Tooltip";
import Spinner from "components/Spinner";
import { KNOWN_PARAMETERS } from "utils/analytics";

export const FilterLabel: FC<{
  text: string;
  tooltip: string;
  icon?: FC<React.SVGProps<SVGSVGElement>>;
  className?: string;
}> = ({ text, tooltip, icon: Icon = QuestionMarkCircleIcon, className }) => (
  <label className={`flex items-center gap-1 ${className ?? ""}`}>
    {text}
    <PureTooltip content={tooltip}>
      <Icon className="w-4 h-4 text-gray-500 cursor-pointer" />
    </PureTooltip>
  </label>
);

export const LoadingOverlay: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (!isLoading) return null;
  return (
    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded pointer-events-none">
      <Spinner size="MD" theme="primary" className="pointer-events-auto" />
    </div>
  );
};

export const CustomizeColumnCheckbox: FC<{
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ id, label, checked, onChange }) => (
  <label htmlFor={id} className="flex items-center gap-2">
    <input
      id={id}
      type="checkbox"
      className="w-4 h-4 accent-rebrandIndigo-600"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span>{label}</span>
  </label>
);

export const ParameterCheckboxGroup: FC<{
  parameters: string[];
  availableParameters: string[];
  onChange: (parameters: string[]) => void;
  disabled?: boolean;
}> = ({ parameters, availableParameters, onChange, disabled = false }) => (
  <div>
    <FilterLabel text="Parameters" tooltip="Filter by specific request parameters." />
    <div className="flex flex-wrap gap-3 mt-3">
      {KNOWN_PARAMETERS.map((param) => {
        const isAvailable = availableParameters.includes(param);
        const isSelected = parameters.includes(param);
        return (
          <label
            key={param}
            className={`flex items-center gap-2 cursor-pointer ${
              !isAvailable && !isSelected ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={(!isAvailable && !isSelected) || disabled}
              onChange={() =>
                onChange(
                  isSelected
                    ? parameters.filter((p) => p !== param)
                    : [...parameters, param],
                )
              }
            />
            {param}
          </label>
        );
      })}
    </div>
  </div>
);
